import React, { useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import type { Identifier, XYCoord } from "dnd-core";
import { CloseIconWrapperSC, InputTextSC, WordSC } from "./styled";
import { IWord } from "../../types";
import { ItemType } from "../../../../index";
import { CloseCircleOutlined } from "@ant-design/icons";

interface WordProps {
  word: IWord;
  index: number;
  moveWord: (from: number, to: number) => void;
  updateWord: (index: number, newText: string) => void;
  activeWordId: string | null;
  setActiveWordId: (id: string | null) => void;
  deleteWord: (index: number) => void;
}

interface DragItem {
  index: number;
  type: string;
  text?: string;
}

export const Word = ({
  word,
  index,
  moveWord,
  updateWord,
  activeWordId,
  setActiveWordId,
  deleteWord,
}: WordProps) => {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(word.text);
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: ItemType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current || item.text) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveWord(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
    drop: (item) => {
      console.log("drop", item);
      // if (item.fromIndex !== undefined) {
      //   moveWord(item.fromIndex, index);
      // } else {
      //   insertWord(item.word, index);
      // }
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: () => {
      return { id: word.id, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  const handleDoubleClick = () => setEditing(true);
  const handleBlur = () => {
    updateWord(index, inputValue);
    setEditing(false);
  };

  const handleClick = () => {
    setActiveWordId(word.id);
  };

  const changeWordToSynonym = (text: string) => () => {
    setActiveWordId(null);
    updateWord(index, text);
  };

  const handleDeleteWord = () => {
    deleteWord(index);
  };

  return (
    <>
      <WordSC
        ref={ref}
        onDoubleClick={handleDoubleClick}
        editing={editing.toString()}
        dragging={isDragging.toString()}
      >
        {editing ? (
          <InputTextSC
            autoFocus
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={(e) => e.key === "Enter" && handleBlur()}
          />
        ) : (
          <span onClick={handleClick}>{word.text}</span>
        )}
        <CloseIconWrapperSC>
          <CloseCircleOutlined onClick={handleDeleteWord} />
        </CloseIconWrapperSC>
        {/*{activeWordId === word.id && word.synonyms && (*/}
        {/*  <DropdownContainerSC>*/}
        {/*    {word.synonyms?.map((item) => (*/}
        {/*      <ISynonymSC key={item.text} onClick={changeWordToSynonym(item.text)}>*/}
        {/*        {item.text}*/}
        {/*      </ISynonymSC>*/}
        {/*    ))}*/}
        {/*  </DropdownContainerSC>*/}
        {/*)}*/}
      </WordSC>
    </>
  );
};
